import { useState, useEffect, useRef } from "react";
import { NavigateOptions, To, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import {
	ArrowDownIcon,
	EnvelopeIcon,
	EyeIcon,
	EyeSlashIcon,
	LockClosedIcon,
	LockOpenIcon,
	MinusIcon,
	PencilIcon,
	PlusIcon,
	PrinterIcon,
} from "@heroicons/react/24/solid";

import { draftTitle } from "../../app/draftHelper";
import { countWords, getParamAsInt, scrollToTop } from "../../app/utils";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { scoreReportDraftAndPromptLink } from "../../app/links";
import { rtkqErrorToString } from "../../app/rtkQueryHelper";
import { clearSharedAlert, setSharedAlert, SharedAlert } from "../../app/siteSlice";
import { scrollToSection, useScrollToSection } from "../../app/useScrollToSection";
import { UserRole } from "../../app/userSlice";
import ModalAlert from "../../components/ModalAlert";
import StackedDate from "../../components/StackedDate";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { Loading } from "../../components/Loading";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import Messages from "../messages/Messages";
import PromptPanel from "../prompts/PromptPanel";
import { DraftStatus, WritingAction } from "../writing/writingApiSlice";
import {
	useDeleteDraftMutation,
	useReviseDraftMutation,
	useUndoFinalizeDraftMutation,
} from "../writing/drafting/draftingApiSlice";
import CommentedEssay from "../writing/drafting/CommentedEssay";
import Organizer from "../writing/organizing/Organizer";
import WritingAnalysis from "./scoreReportSections/WritingAnalysis";
import PlagiarismAnalysis from "./scoreReportSections/PlagiarismAnalysis";
import TotalScoreGraphic from "./scoreReportSections/TotalScoreGraphic";
import NavigateToOtherEssays from "./scoreReportSections/NavigateToOtherEssays";
import PeerReviews from "./scoreReportSections/PeerReviews";
import ScoreReportScores from "./scoreReportSections/Scores";
import DraftList from "./scoreReportSections/DraftList";
import {
	ScoreReportViewModel,
	ScoredCategoryViewModel,
	WritingAnalysisViewModel,
	useGetAllWritersForPromptQuery,
	useScoreReportByEssayQuery,
	useScoreReportQuery,
	useSendReviewRequestMutation,
} from "./scoreReportApiSlice";

export default function ScoreReport() {
	const [queryParams] = useSearchParams();
	const draftIdParam = getParamAsInt(queryParams, "draftId");
	const essayIdParam = getParamAsInt(queryParams, "essayId");
	const promptIdParam = getParamAsInt(queryParams, "promptId");

	const [view, setView] = useState<string>("legacy");
	const [draftId, setDraftId] = useState<number>(0);
	const [showOrganizer, setShowOrganizer] = useState(false);
	const [showAll, setShowAll] = useState(true);
	const [scoreReport, setScoreReport] = useState<ScoreReportViewModel | undefined>();
	const [wordCount, setWordCount] = useState(0);
	const [analysis, setAnalysis] = useState<WritingAnalysisViewModel | undefined>();
	const [sharedAlertMessage, setSharedAlertMessage] = useState<SharedAlert>();
	const [fetchError, setFetchError] = useState<string | undefined>();
	const navigating = useRef(false);

	const location = useLocation();
	const navigate = useNavigate();

	const dispatch = useAppDispatch();
	const sharedAlert = useAppSelector((state) => state.site.sharedAlert);
	const config = useAppSelector((state) => state.site.config);
	const role = useAppSelector((state) => state.user.role);

	const refPrintAreaOne = useRef<HTMLDivElement>(null);
	const refPrintAreaTwo = useRef<HTMLDivElement>(null);

	const byDraftResult = useScoreReportQuery(draftIdParam ?? skipToken);
	const byEssayResult = useScoreReportByEssayQuery(essayIdParam ?? skipToken);

	const allWritersForPromptResult = useGetAllWritersForPromptQuery(promptIdParam ?? skipToken);
	const allWritersForPrompt = allWritersForPromptResult.data;

	const [callReviseDraft] = useReviseDraftMutation();
	const [callDeleteDraft] = useDeleteDraftMutation();
	const [callUndoFinalizeDraft] = useUndoFinalizeDraftMutation();

	useScrollToSection();

	useEffect(() => {
		if (sharedAlert && !navigating.current) {
			setSharedAlertMessage(sharedAlert);
			dispatch(clearSharedAlert());
		}
	}, [sharedAlert, navigating]);

	useEffect(() => {
		const data = byDraftResult.data ?? byEssayResult.data;
		if (!data) return;

		setDraftId(data.draft.id);
		setScoreReport(data);
		setWordCount(countWords(data.pureTextForWordCount));
		setShowAll(data.drafts.length > 5 ? false : true);

		if (location.state != null) {
			if (
				(location.state.section === "EssayMessages" ||
					location.state.section === "OrganizerSection") &&
				!data.draftScore &&
				data.organizer &&
				data.organizer.htmlTemplate
			) {
				setShowOrganizer(true);
			} else {
				setShowOrganizer(false);
			}
		}

		setAnalysis({
			id: data.draftScore?.id,
			showScore: data.draftScore !== null,
			genre: data.prompt.genre.name,
			categories: data.draftScore?.categoryScores,
			cSharpScoreFormat: data.draftScore?.cSharpScoreFormat,
			decimalPlacesDisplayed: data.draftScore?.decimalPlacesDisplayed,
			canScoreProctorScorableCategories: data.canScoreProctorScorableCategories,
			staticFeedback: data.staticFeedback,
			targetedFeedback: data.targetedFeedback,
			llmFeedback: data.llmFeedback,
			feedbackTutorials: data.feedbackTutorials,
		});
	}, [byDraftResult.data, byEssayResult.data]);

	useEffect(() => {
		if (!byDraftResult.isError && !byEssayResult.isError) return;

		let errorMsg = "";
		if (byDraftResult.isError) {
			errorMsg = rtkqErrorToString(byDraftResult.error);
		}
		if (byEssayResult.isError) {
			errorMsg = rtkqErrorToString(byEssayResult.error);
		}

		setFetchError(errorMsg);
	}, [byDraftResult.isError, byEssayResult.isError]);

	function doNavigate(to: To, options?: NavigateOptions) {
		navigating.current = true;
		navigate(to, options);
	}

	function doNavigateAfterDeleteAllEssays() {
		doNavigate(`/writing`);

		dispatch(
			setSharedAlert({
				level: AlertLevel.Info,
				message: `An essay for ${scoreReport?.writerName} was deleted when its last draft was deleted.`,
			})
		);
	}

	function handleProctorScore(categoryScore: ScoredCategoryViewModel) {
		if (!analysis) return;
		const categoryScores = analysis.categories.map((obj) => {
			if (obj.id === categoryScore.id) {
				return categoryScore;
			}
			return obj;
		});
		const _analysis = { ...analysis, categories: categoryScores };
		setAnalysis(_analysis);
	}

	function handlePrintScoreReport() {
		window.print();
	}

	function handlePrintEssay() {
		const mywindow = window.open("", "PRINT", "height=400,width=600");
		if (!mywindow) return false;
		if (!refPrintAreaOne || !refPrintAreaOne.current) return false;
		if (!refPrintAreaTwo || !refPrintAreaTwo.current) return false;

		const head = document.head.innerHTML
			.replace(/<title>.+<\/title>/, "")
			.replace(/<script type="module">[\s\S]+<\/script>/m, "")
			.replace(/<script type="module" src="[^"]+">[\s\S]+<\/script>/m, "");

		const title =
			draftTitle(scoreReport?.commentedEssay.title).trim().length > 0
				? draftTitle(scoreReport?.commentedEssay.title)
				: (scoreReport?.prompt.title ?? "No Title");
		const docTitle = `${scoreReport?.writerName} - ${title}`;

		const doc = `
<html>
<head>
  <title>${docTitle}</title>
  ${head}
  <style>
    #areaTwo .toolbar { display: none; }
  </style>
</head>
<body class="m-8">
  <div id="areaOne" class="mt-8">${refPrintAreaOne.current.innerHTML}</div>
  <div id="areaTwo" class="mt-8">${refPrintAreaTwo.current.innerHTML}</div>
</body>
</html>`;

		mywindow.document.write(doc);
		mywindow.document.close(); // necessary for IE >= 10
		mywindow.focus(); // necessary for IE >= 10*/
		mywindow.print();
		mywindow.close();

		return true;
	}

	function handleContinueEssay(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();

		if (!draftId) return;
		doNavigate(`/writing/write?draftId=${draftId}`);
	}

	function handleReviseEssay() {
		if (!draftId) return;
		callReviseDraft(draftId)
			.unwrap()
			.then((payload) => {
				doNavigate(`/writing/write?draftId=${payload.draftId}`);
			})
			.catch((reason) => {
				console.log(`reviseDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handleFinalizeEssay() {
		if (!draftId) return;
		doNavigate(`/writing/preview?draftId=${draftId}`);
	}

	function handleUndoFinalizeEssay() {
		if (!draftId) return;
		callUndoFinalizeDraft(draftId)
			.unwrap()
			.catch((reason) => {
				console.log(`undoFinalizeDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handlePreviewEssay() {
		if (!draftId) return;
		doNavigate(`/writing/preview?draftId=${draftId}`);
	}

	function handleDeleteDraft() {
		if (!draftId) return;
		callDeleteDraft(draftId)
			.unwrap()
			.then(() => {
				const draft = scoreReport?.drafts.findLast((d) => d.id != draftId);
				if (draft) {
					handleClearSharedAlert();
					doNavigate(
						promptIdParam
							? scoreReportDraftAndPromptLink(promptIdParam, draft.id)
							: `/scoreReport?draftId=${draft.id}`
					);
				} else {
					const hasOtherStudents = allWritersForPrompt && allWritersForPrompt.length > 1;

					if (hasOtherStudents && promptIdParam) {
						const currentIndex = allWritersForPrompt.findIndex(
							(w) =>
								w.actionId ===
								scoreReport?.drafts[scoreReport.drafts.length - 1]?.id
						);
						const nextIndex =
							currentIndex < allWritersForPrompt.length - 1
								? currentIndex + 1
								: currentIndex - 1;

						if (nextIndex >= 0 && nextIndex < allWritersForPrompt.length) {
							const nextWriter = allWritersForPrompt[nextIndex];
							navigate(
								scoreReportDraftAndPromptLink(promptIdParam, nextWriter.actionId),
								{
									state: {
										section:
											nextWriter.action === WritingAction.Organize
												? "OrganizerSection"
												: null,
									},
								}
							);

							dispatch(
								setSharedAlert({
									level: AlertLevel.Info,
									message: `An essay for ${allWritersForPrompt[currentIndex].writerName} was deleted when its last draft was deleted. You are now viewing the essay for ${allWritersForPrompt[nextIndex].writerName}.`,
								})
							);
						} else {
							doNavigateAfterDeleteAllEssays();
						}
					} else {
						doNavigateAfterDeleteAllEssays();
					}
				}
			})
			.catch((reason) => {
				console.log(`deleteDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handleChangeDraft(draftId: number) {
		if (!scoreReport) return;

		const draft = scoreReport.drafts.find((d) => d.id === draftId);
		if (!draft) return;

		if (draft.status === DraftStatus.Incomplete) {
			if (!scoreReport.dueDateExceeded || !scoreReport.dueDateRequired) {
				doNavigate(`/writing/write?draftId=${draft.id}`);
			}
		} else if (draft.hasLegacyHtmlFormat) {
			doNavigate(`/writing/preview?draftId=${draft.id}`);
		}

		handleClearSharedAlert();
		doNavigate(
			promptIdParam
				? scoreReportDraftAndPromptLink(promptIdParam, draft.id)
				: `/scoreReport?draftId=${draft.id}`
		);
	}

	const [requestReview] = useSendReviewRequestMutation();

	function confirmRequestReview() {
		if (draftId) {
			requestReview(draftId);
		}
	}

	function handleClearSharedAlert() {
		setSharedAlertMessage(undefined);
	}

	if (draftIdParam === undefined && essayIdParam === undefined) return null;

	return (
		<main>
			{role === UserRole.Proctor && promptIdParam && allWritersForPrompt && (
				<NavigateToOtherEssays
					lastDraftId={scoreReport?.drafts[scoreReport.drafts.length - 1]?.id ?? 0}
					promptId={promptIdParam}
					allWriters={allWritersForPrompt}
					showEssayCount={true}
					clearSharedAlert={handleClearSharedAlert}
					className="mb-2"
				/>
			)}
			{sharedAlertMessage && (
				<AlertMessage
					className="mt-2 mb-4"
					type={sharedAlertMessage.level}
					message={sharedAlertMessage.message}
					showIcon={sharedAlertMessage.showIcon}
					onClear={handleClearSharedAlert}
				/>
			)}
			<div className="flex justify-between">
				<div>
					<h1>Score Report</h1>
				</div>
				<div className="flex flex-col items-end gap-2 print:hidden">
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						options={[
							{ value: "legacy", label: "Legacy" },
							{ value: "raw", label: "Raw Data" },
						]}
						fieldClasses="w-64"
					/>
					{view === "legacy" && scoreReport && analysis && wordCount > 0 && (
						<button
							id="print-report-button"
							className="btn btn-neutral ml-4"
							onClick={handlePrintScoreReport}>
							<PrinterIcon className="h-4 inline mr-2" />
							Print Report
						</button>
					)}
				</div>
			</div>
			{fetchError && (
				<ModalAlert
					open={true}
					title={"Error"}
					message={fetchError}
					type={AlertLevel.Error}
					confirmLabel={"OK"}
					onConfirm={() => doNavigate(`/home`)}
				/>
			)}
			<Loading loading={byDraftResult.isLoading || byEssayResult.isLoading} />
			{view === "legacy" && scoreReport && analysis && (
				<>
					<section id="PromptSection" ref={refPrintAreaOne}>
						<div className="flex">
							<StackedDate
								className="ml-8 mr-24"
								date={scoreReport.draft.createDate}
							/>
							<div>
								<h2>{scoreReport.prompt.title}</h2>
								<div>by {scoreReport.writerName}</div>
								<div>for {scoreReport.course.name}</div>
							</div>
						</div>
						<div>
							<PromptPanel
								name={scoreReport.prompt.title}
								showName={false}
								renderedText={scoreReport.prompt.renderedText}
								stimulusMaterials={scoreReport.stimulusMaterials}
								showGenre={false}></PromptPanel>
						</div>
					</section>
					<div className="relative mt-8">
						{scoreReport.organizer && scoreReport.organizer.htmlTemplate && (
							<section id="OrganizerSection" className="flex z-40">
								<div className="w-3/4">
									{showOrganizer && (
										<Organizer
											className="py-4 relative"
											html={scoreReport.organizer.htmlTemplate}
											response={scoreReport.organizer.organizerResponse}
											readonly={true}
										/>
									)}
								</div>
								<div className="w-1/4 ml-8 mt-3 absolute -right-2">
									<button
										id="show-organizer-button"
										className="btn btn-neutral text-left print:!hidden"
										onClick={() => setShowOrganizer(!showOrganizer)}>
										{showOrganizer ? (
											<span>
												<EyeSlashIcon className="h-4 inline mr-2" />
												Hide Organizer
											</span>
										) : (
											<span>
												<EyeIcon className="h-4 inline mr-2" />
												Show Organizer
											</span>
										)}
									</button>
								</div>
							</section>
						)}
						<section id="DraftArea" className="flex">
							<div className="w-3/4" ref={refPrintAreaTwo}>
								<CommentedEssay
									commentedEssay={scoreReport.commentedEssay}
									isScoreReport={true}
								/>
							</div>
							<div className="w-1/4 ml-8 mt-4">
								{scoreReport.draftScore && (
									<div>
										<h2 className="mt-12 relative z-1">Total Score</h2>
										<div
											className="relative -top-8 -left-8"
											id="total-score-graphic">
											<TotalScoreGraphic
												totalScore={scoreReport.draftScore.totalScore}
												maxScore={
													scoreReport.prompt.genre.name ==
													"Constructed Response"
														? 3
														: 30
												}></TotalScoreGraphic>
										</div>
										<button
											id="view-scores-button"
											className="btn btn-neutral print:hidden relative -top-40 my-4"
											onClick={() => scrollToSection("DraftScores")}>
											<ArrowDownIcon className="h-4 inline mr-2" />
											View Scores
										</button>
									</div>
								)}

								<div
									className={`${scoreReport.draftScore ? "-mt-44" : ""} relative z-1`}>
									<div
										className={`${scoreReport.draftScore ? "mt-8" : "mt-12"} text-xl font-medium`}>
										Word Count:&nbsp;
										<span id="word-count" className="font-normal">
											{wordCount}
										</span>
									</div>

									<div className="mb-6">
										<h2 className="mt-6">Submissions</h2>

										<div className="pb-2">
											{scoreReport.remainingRevisions} submissions left
										</div>
										<DraftList
											drafts={scoreReport.drafts}
											currentDraft={scoreReport.draft.draftNumber}
											status={scoreReport.draft.status}
											showAll={showAll}
											draftsWithReviews={scoreReport.reviewedTestIds}
											hasLegacyHtmlFormat={
												scoreReport.draft.hasLegacyHtmlFormat
											}
											onChangeDraft={handleChangeDraft}
										/>
										{scoreReport.drafts.length > 5 && (
											<div
												className="mt-1 text-black cursor-pointer"
												onClick={() => setShowAll((v) => !v)}>
												{showAll ? (
													<span className="relative flex items-center font-medium">
														<MinusIcon className="h-4 inline mr-0" />
														&nbsp;Show Less
													</span>
												) : (
													<span className="relative flex items-center font-medium">
														<PlusIcon className="h-4 inline mr-0 " />
														&nbsp;Show All
													</span>
												)}
											</div>
										)}
									</div>

									{scoreReport.draft.status === DraftStatus.Incomplete &&
										scoreReport.isWriter && (
											<button
												id="continue-essay-button"
												className="btn btn-neutral mt-4 !block print:!hidden"
												onClick={handleContinueEssay}
												disabled={
													scoreReport.dueDateExceeded &&
													scoreReport.dueDateRequired
												}>
												<PencilIcon className="h-4 inline mr-2" />
												Continue Writing
											</button>
										)}

									{(scoreReport.draft.status === DraftStatus.Scored ||
										scoreReport.draft.status === DraftStatus.ScoringFailed) &&
										scoreReport.isWriter && (
											<>
												<ButtonWithConfirm
													id="revise-button"
													className="btn btn-primary mt-4 !block print:!hidden"
													disabled={
														(scoreReport.dueDateExceeded &&
															scoreReport.dueDateRequired) ||
														scoreReport.remainingRevisions === 0
													}
													message="Are you sure you want to revise this essay?"
													type={AlertLevel.Info}
													confirmLabel="Yes"
													cancelLabel="No"
													onConfirm={handleReviseEssay}>
													<PencilIcon className="h-4 inline mr-2" />
													Revise Essay
												</ButtonWithConfirm>
											</>
										)}

									{scoreReport.draft.status === DraftStatus.Scored &&
										scoreReport.canUserFinalize && (
											<button
												id="finalize-button"
												className="btn btn-alert mt-4 !block print:!hidden"
												onClick={handleFinalizeEssay}>
												<LockClosedIcon className="h-4 inline mr-2" />
												Finalize Essay
											</button>
										)}

									{scoreReport.canUserPreview &&
										!scoreReport.canUserFinalize &&
										scoreReport.draft.status !== DraftStatus.Deleted &&
										wordCount > 0 && (
											<button
												id="preview-button"
												className="btn btn-neutral mt-4 !block print:!hidden"
												onClick={handlePreviewEssay}>
												<EyeIcon className="h-4 inline mr-2" />
												Preview Draft
											</button>
										)}

									{wordCount > 0 && (
										<button
											id="print-draft-button"
											className="btn btn-neutral mt-4 !block print:!hidden"
											onClick={handlePrintEssay}>
											<PrinterIcon className="h-4 inline mr-2" />
											Print Draft
										</button>
									)}

									{scoreReport.draft.status === DraftStatus.Finalized &&
										scoreReport.canUserRetract && (
											<ButtonWithConfirm
												id="undo-finalize-button"
												className="btn btn-alert mt-4 !block print:!hidden"
												message="You are about to retract a student’s finalized essay so they may continue writing. Are you sure you want to undo finalization?"
												type={AlertLevel.Warning}
												confirmLabel="Yes"
												cancelLabel="No"
												onConfirm={handleUndoFinalizeEssay}>
												<LockOpenIcon className="h-4 inline mr-2" />
												Undo Finalize
											</ButtonWithConfirm>
										)}

									{scoreReport.canDeleteDraft && (
										<ButtonWithConfirm
											id="delete-button"
											className="btn btn-danger mt-4 !block print:!hidden"
											message="Are you sure you want to delete this draft?"
											type={AlertLevel.Error}
											confirmLabel="Yes"
											cancelLabel="No"
											onConfirm={handleDeleteDraft}>
											<span className="h-4 inline mr-3">✘</span>
											Delete Draft
										</ButtonWithConfirm>
									)}

									{scoreReport.showPeerReviewSection && (
										<>
											<h2 className="mt-8">Peer Review</h2>
											{scoreReport.totalRequestedReviewCount == 0 &&
												scoreReport.isWriter && (
													<ButtonWithConfirm
														id="request-reviews-button"
														className="btn btn-neutral mt-4 !block print:!hidden"
														disabled={
															(scoreReport.dueDateExceeded &&
																scoreReport.dueDateRequired) ||
															scoreReport.remainingRevisions === 0
														}
														message="Are you sure you want to request peer review for your essay?"
														type={AlertLevel.Warning}
														confirmLabel="Yes"
														cancelLabel="Cancel"
														onConfirm={confirmRequestReview}>
														<EnvelopeIcon className="h-4 inline mr-2" />
														Request Review
													</ButtonWithConfirm>
												)}
											{scoreReport.totalRequestedReviewCount == 0 &&
												!scoreReport.isWriter && <p>Not requested</p>}
											{scoreReport.totalRequestedReviewCount > 0 && (
												<>
													<p>Sent {scoreReport.dateSent}</p>
													<p>
														{scoreReport.reviewCount} of{" "}
														{scoreReport.totalRequestedReviewCount}{" "}
														complete
													</p>
												</>
											)}
										</>
									)}
								</div>
							</div>
						</section>
					</div>
					{scoreReport.showPeerReviewSection &&
						scoreReport.completedReviews.length > 0 && (
							<>
								<hr className="my-4" />
								<PeerReviews
									draftId={scoreReport.draft.id}
									reviews={scoreReport.completedReviews}
								/>
							</>
						)}
					<hr className="my-4" />
					<section id="DraftScores">
						<ScoreReportScores
							analysis={analysis}
							draftScore={scoreReport.draftScore}
							showDraftNorm={scoreReport.showNormativeScores}
							draftNorm={scoreReport.draftScoreNorm}
							normSupportLinkSuffix={
								(role == UserRole.Writer
									? config?.writerNormsSupportUrlSuffix
									: config?.proctorNormsSupportUrlSuffix) ?? ""
							}
							genre={scoreReport.prompt.genre}></ScoreReportScores>
					</section>
					<hr className="my-4" />
					<section id="WritingAnalysis">
						<WritingAnalysis analysis={analysis} onProctorScore={handleProctorScore} />
					</section>
					<hr className="my-4" />
					{scoreReport.plagiarismAnalysis != null && (
						<>
							<section id="PlagiarismAnalysis">
								<PlagiarismAnalysis analysis={scoreReport.plagiarismAnalysis} />
							</section>
							<hr className="my-4" />
						</>
					)}
					<section id="EssayMessages">
						<Messages draftId={draftId} />
					</section>
					{role === UserRole.Proctor && promptIdParam && (
						<NavigateToOtherEssays
							lastDraftId={
								scoreReport?.drafts[scoreReport.drafts.length - 1]?.id ?? 0
							}
							promptId={promptIdParam}
							allWriters={allWritersForPrompt}
							showEssayCount={false}
							clearSharedAlert={handleClearSharedAlert}
							className="mt-3"
						/>
					)}
					<div className="mt-4 text-center print:hidden">
						[
						<a className="cursor-pointer" onClick={scrollToTop}>
							Return to top
						</a>
						]
					</div>
				</>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Score Report Data By Draft" result={byDraftResult} />
					<RtkqResultViewer heading="Score Report Data By Essay" result={byEssayResult} />
				</>
			)}
		</main>
	);
}
